import {
  ExperienceAnchor,
  ExperienceTemplateMessage,
  isEventDrivenTiming,
  ITaskTemplate,
  ITimelineItem,
  UserField
} from '@dadohr/shared/domainTypes/experience';
import { Button } from '@material-ui/core';
import { Calendar } from 'lucide-react';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { SwitchWithLabel } from '../../../../../../components/SwitchWithLabel';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../layout/Flex';
import { useFeatureEnabled } from '../../../../../../services/features';
import { ONE_DAY } from '../../../../../../services/time';
import { UserTimezoneHandlingInfoBox } from '../../../UserTimezoneHandlongInfoBox';
import { UserTimezoneHandlingPicker } from '../../../UserTimezoneHnadlingPicker';
import {
  getTimelineStartTimeFromTaskBuilderContext,
  TaskBuilderContext
} from '../types';
import { RuleRenderer, RuleBuilderConfig } from '../../../RuleBuilder';
import { SettingsSection, SideContainer } from './layout';
import { SideContainerHeader } from './SideContainerHeader';
import { TaskSettingsOverviewSection } from './TaskSettingsOverviewSection';
import * as UX from '../../newux/kit';
import { FixedTimingSummary } from './FixedTimingSummary';
import { TimelineItemTiming } from './TimelineItemTiming';
import { DaySelector, TimePicker } from './TimeSelectors';
import { TriggerList } from './TriggerList';

const ICON = Calendar;

const MessageTimingInner = ({
  value,
  allTasks,
  initialUserTimezoneStartTime,
  onGoToOtherItem,
  ruleConfig,
  anchor,
  tz
}: {
  value: ExperienceTemplateMessage;
  allTasks: ITaskTemplate[];
  initialUserTimezoneStartTime: {
    hour: number;
    minute: number;
  } | null;
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
  ruleConfig?: RuleBuilderConfig<UserField>;
  // NEW_UX: when given, the fixed-timing line is phrased in the author's chosen
  // mode (before/after milestone, calendar date). Classic callers leave it out.
  anchor?: ExperienceAnchor;
  tz?: string;
}) => {
  const { timing, preferUserTimezone } = value;
  const { t } = useUnsafeTranslation();

  const userTimezoneHandlingEnabled = useFeatureEnabled(
    'USER_BASED_TIMEZONES_V1'
  );

  if (timing.type === 'fixed') {
    const { start, time } = timing;

    const sT =
      time ||
      (userTimezoneHandlingEnabled ? initialUserTimezoneStartTime : null);
    return (
      <div>
        {anchor && tz ? (
          <FixedTimingSummary timing={timing} anchor={anchor} tz={tz} />
        ) : (
          <UX.TimingSummaryText
            text={t('**Day {{ day }}** of experience', {
              day: Math.round(start / ONE_DAY) + 1
            })}
          />
        )}
        {sT && (
          <span>
            {' ' + t('at') + ' '}
            {moment().hour(sT.hour).minute(sT.minute).format('hh:mm a')}
            {preferUserTimezone && ` ${t(`in user's timezone`)}`}
          </span>
        )}
      </div>
    );
  }

  if (timing.type === 'dataChange') {
    const day = Math.round((timing.earliestStart ?? 0) / ONE_DAY) + 1;
    const fieldLabel = timing.watchField
      ? ruleConfig?.[timing.watchField]?.label
      : undefined;
    if (timing.watchField) {
      return (
        <UX.TimingSummaryText
          text={t('When **{{ field }}** changes, on or after **day {{ day }}** of experience', {
            field: fieldLabel || t('a field'),
            day
          })}
        />
      );
    }
    return (
      <div>
        <UX.TimingSummaryText
          text={t('When conditions met, on or after **day {{ day }}** of experience:', {
            day
          })}
        />
        {ruleConfig && (
          <div style={{ marginTop: 4 }}>
            <RuleRenderer
              value={timing.rules ?? null}
              config={ruleConfig}
              variant="oneline"
            />
          </div>
        )}
      </div>
    );
  }

  if (isEventDrivenTiming(timing)) {
    return (
      <TriggerList
        timing={timing}
        allTasks={allTasks}
        onGoToOtherItem={onGoToOtherItem}
      />
    );
  }

  console.warn(`Cannot render timing type - ${timing}`);
  return null;
};

/**
 * Read-only "Send" section for the NEW_UX message viewer, mirroring the task
 * viewer's timing renderer. Reuses MessageTimingInner's fixed/dataChange/
 * event-driven text + a catch-up note.
 */
export const MessageViewerSendDate = ({
  value,
  allTasks,
  initialUserTimezoneStartTime,
  onGoToOtherItem,
  ruleConfig,
  anchor,
  tz
}: {
  value: ExperienceTemplateMessage;
  allTasks: ITaskTemplate[];
  initialUserTimezoneStartTime: { hour: number; minute: number } | null;
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
  ruleConfig?: RuleBuilderConfig<UserField>;
  anchor: ExperienceAnchor;
  tz: string;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <div style={{ marginBottom: 16 }}>
      <UX.FieldLabel>{t('Start')}</UX.FieldLabel>
      <div
        style={{
          color: UX.tokens.color.grey700,
          fontSize: 16,
          fontWeight: UX.tokens.font.weightScale.regular
        }}
      >
        <MessageTimingInner
          value={value}
          allTasks={allTasks}
          initialUserTimezoneStartTime={initialUserTimezoneStartTime}
          onGoToOtherItem={onGoToOtherItem}
          ruleConfig={ruleConfig}
          anchor={anchor}
          tz={tz}
        />
      </div>
      {value.timing.type === 'fixed' && value.catchup?.disabled && (
        <div
          style={{
            marginTop: 4,
            fontStyle: 'italic',
            color: UX.tokens.color.grey500
          }}
        >
          {t(`Won't send in catch-up mode`)}
        </div>
      )}
    </div>
  );
};

export const MessageSettingsOverviewSectionSendDate = ({
  title,
  value,
  onEdit,
  divider,
  initialUserTimezoneStartTime,
  allTasks
}: {
  title: string;
  value: ExperienceTemplateMessage;
  onEdit?: () => void;
  divider?: boolean;
  initialUserTimezoneStartTime: {
    hour: number;
    minute: number;
  } | null;
  allTasks: ITaskTemplate[];
}) => {
  return (
    <TaskSettingsOverviewSection
      icon={ICON}
      title={title}
      onEdit={onEdit}
      divider={divider}
    >
      <MessageTimingInner
        value={value}
        allTasks={allTasks}
        initialUserTimezoneStartTime={initialUserTimezoneStartTime}
      />
    </TaskSettingsOverviewSection>
  );
};

export const MessageSettingsSendDateV2 = ({
  title,
  description,
  onBack,

  value,
  onChange,
  expTpl,
  onGoToOtherItem,
  newUx = false
}: {
  title: string;
  description?: string;
  onBack: () => void;

  value: ExperienceTemplateMessage;
  onChange: React.Dispatch<React.SetStateAction<ExperienceTemplateMessage>>;
  expTpl: TaskBuilderContext;
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
  newUx?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  if (newUx) {
    // NEW_UX Timing panel for messages: send timing (kit radio-cards) + catch-up,
    // rendered bare (the shell owns the panel header). Reuses the same handlers.
    return (
      <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
        <UX.PanelHeader
          icon={<Calendar size={17} />}
          title={t('Timing & Triggers')}
          hint={t('When this message is sent.')}
        />
        <UX.SectionLabel style={{ marginTop: 0 }}>{t('Start')}</UX.SectionLabel>
        <TimelineItemTiming
          value={value.timing}
          onChange={(nextTiming) =>
            onChange((v) => ({ ...v, timing: nextTiming }))
          }
          tplContext={expTpl}
          onGoToOtherItem={onGoToOtherItem}
          newUx
        />
        {value.timing.type === 'fixed' && (
          <>
            <UX.SectionLabel>{t('Catch-up mode')}</UX.SectionLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <UX.Toggle
                on={value.catchup.disabled}
                ariaLabel={t(`Don't send this item in catch-up mode`)}
                onChange={(nextChecked) =>
                  onChange((v) => ({
                    ...v,
                    catchup: { ...v.catchup, disabled: nextChecked }
                  }))
                }
              />
              <span style={{ fontSize: 13 }}>
                {t(`Don't send this item in catch-up mode`)}
              </span>
            </div>
          </>
        )}
      </div>
    );
  }
  return (
    <SideContainer>
      <SideContainerHeader
        def={{
          label: title,
          description,
          icon: ICON
        }}
        onBack={onBack}
      />
      <SettingsSection>
        <FlexContainerVertical fullWidth spacing={3}>
          <TimelineItemTiming
            value={value.timing}
            /* TODO make sure to change batch mode if we're going triggered */
            onChange={(nextTiming) =>
              onChange((v) => ({ ...v, timing: nextTiming }))
            }
            tplContext={expTpl}
            onGoToOtherItem={onGoToOtherItem}
          />

          {value.timing.type === 'fixed' && (
            <CustomInputLabel label={t('Catch-up mode ')} fullWidth>
              <SwitchWithLabel
                label={t(`Don't send this item in catch-up mode`)}
                checked={value.catchup.disabled}
                onChange={(nextChecked) =>
                  onChange((v) => ({
                    ...v,
                    catchup: {
                      ...v.catchup,
                      disabled: nextChecked
                    }
                  }))
                }
              />
            </CustomInputLabel>
          )}
        </FlexContainerVertical>
      </SettingsSection>
    </SideContainer>
  );
};

export const MessageSettingsSendDate = ({
  title,
  description,
  onBack,

  value,
  onChange,
  expTpl
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: ExperienceTemplateMessage;
  onChange: React.Dispatch<React.SetStateAction<ExperienceTemplateMessage>>;
  expTpl: TaskBuilderContext;
}) => {
  const userTimezoneHandlingEnabled = useFeatureEnabled(
    'USER_BASED_TIMEZONES_V1'
  );
  const { t } = useUnsafeTranslation();

  const {
    onChangeSendAt,
    onChangeStartTime,
    onClearStartTime,
    onSetDefaultStartTime,
    onChangePreferUserTimezone
  } = useMemo(() => {
    return {
      onChangeSendAt: (nextSendAt: number) =>
        onChange((v) => {
          if (v.timing.type !== 'fixed') {
            return v;
          }
          return {
            ...v,
            timing: {
              ...v.timing,
              start: nextSendAt
            }
          };
        }),
      onChangeStartTime: (startTime: { hour: number; minute: number }) =>
        onChange((v) => {
          if (v.timing.type !== 'fixed') {
            return v;
          }
          return {
            ...v,
            timing: {
              ...v.timing,
              time: startTime
            }
          };
        }),
      onSetDefaultStartTime: () =>
        onChange((v) => {
          if (v.timing.type !== 'fixed') {
            return v;
          }
          return {
            ...v,
            timing: { ...v.timing, time: { hour: 9, minute: 0 } }
          };
        }),
      onClearStartTime: () =>
        onChange((v) => {
          if (v.timing.type !== 'fixed') {
            return v;
          }
          return { ...v, timing: { ...v.timing, time: null } };
        }),
      onChangePreferUserTimezone: (nextValue: boolean) =>
        onChange((v) => ({ ...v, preferUserTimezone: nextValue }))
    };
  }, [onChange]);

  return (
    <SideContainer>
      <SideContainerHeader
        def={{
          label: title,
          description,
          icon: ICON
        }}
        onBack={onBack}
      />
      {value.timing.type === 'fixed' && (
        <SettingsSection>
          <FormRow>
            <CustomInputLabel
              label={t('Start')}
              info={
                !userTimezoneHandlingEnabled
                  ? t(
                      'Messages without a specified time will send at the same time as when the experience started'
                    )
                  : undefined
              }
              fullWidth
            >
              <DaySelector
                value={value.timing.start}
                onChange={onChangeSendAt}
              />
              {!userTimezoneHandlingEnabled && (
                <>
                  {!value.timing.time && (
                    <Button
                      color="primary"
                      onClick={onSetDefaultStartTime}
                      size="small"
                    >
                      {t('Add Time')}
                    </Button>
                  )}
                  {value.timing.time && (
                    <TimePicker
                      t={value.timing.time}
                      minuteStep={10}
                      onChange={onChangeStartTime}
                      onClear={onClearStartTime}
                    />
                  )}
                </>
              )}
            </CustomInputLabel>
          </FormRow>
          {userTimezoneHandlingEnabled && (
            <>
              <FormRow>
                <TimePicker
                  t={
                    value.timing.time ??
                    getTimelineStartTimeFromTaskBuilderContext(expTpl) ?? {
                      hour: 9,
                      minute: 0
                    }
                  }
                  minuteStep={10}
                  onChange={onChangeStartTime}
                />
                <UserTimezoneHandlingPicker
                  value={value.preferUserTimezone}
                  onChange={onChangePreferUserTimezone}
                  defaultTz={expTpl.tz}
                  userLabel={t('user')}
                />
              </FormRow>
              {value.preferUserTimezone && (
                <FormRow>
                  <UserTimezoneHandlingInfoBox tz={expTpl.tz} />
                </FormRow>
              )}
            </>
          )}
        </SettingsSection>
      )}
    </SideContainer>
  );
};
